import { FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import useCreateCommunity from "../hooks/useCreateCommunity";
import useUploadCommunityLogo from "../hooks/useUploadCommunityLogo";
import useUserStore from "../store/userStore";
import "./CreateCommunity.css";

const CreateCommunity = () => {
  const navigate = useNavigate();
  const token = useUserStore((state) => state.token);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [logo, setLogo] = useState<File | null>(null);
  const [formError, setFormError] = useState("");
  const {
    mutate: uploadLogo,
    isPending: isUploading,
    error: uploadError,
  } = useUploadCommunityLogo(token);
  const {
    mutate: createCommunity,
    isPending: isCreating,
    error: createError,
  } = useCreateCommunity();

  const isSaving = isUploading || isCreating;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError("");

    if (!logo) {
      setFormError("Logo is required.");
      return;
    }

    const formData = new FormData();
    const extension = logo.name.split(".").pop();
    const safeName = name.trim().replace(/\s+/g, "_").toLowerCase();
    const renamedLogo = new File([logo], `${Date.now()}_${safeName}.${extension}`, {
      type: logo.type,
    });

    formData.append("file", renamedLogo);

    uploadLogo(formData, {
      onSuccess: (logoPath) => {
        createCommunity(
          {
            token,
            community: {
              name: name.trim(),
              description: description.trim(),
              logo: logoPath,
            },
          },
          {
            onSuccess: () => {
              navigate("/community");
            },
          }
        );
      },
    });
  };

  return (
    <main className="create-community-page">
      <section className="create-community-panel">
        <header className="create-community-header">
          <h1>Create Community</h1>
          <button
            className="create-community-secondary-button"
            type="button"
            onClick={() => navigate("/community")}
          >
            Back
          </button>
        </header>

        <form className="create-community-form" onSubmit={handleSubmit}>
          <label>
            Name
            <input
              type="text"
              value={name}
              onChange={(event) => setName(event.target.value)}
              minLength={3}
              maxLength={80}
              required
            />
          </label>

          <label>
            Logo
            <input
              type="file"
              accept="image/*"
              onChange={(event) => setLogo(event.target.files?.[0] ?? null)}
              required
            />
          </label>

          <label>
            Description
            <textarea
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              maxLength={1000}
              required
            />
          </label>

          {(formError || uploadError || createError) && (
            <p className="create-community-error">
              {formError || "Could not create community."}
            </p>
          )}

          <button
            className="create-community-primary-button"
            type="submit"
            disabled={isSaving}
          >
            {isSaving ? "Creating..." : "Create Community"}
          </button>
        </form>
      </section>
    </main>
  );
};

export default CreateCommunity;
